import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Routes, Route } from "react-router";
import { App, RequirePolicy, createEmbed } from "@livefree/applications";
import { ReactiveCore, DexieDatabaseProvider } from "@livefree/reactive";
import { dexieRequestBrokerAddon } from "@livefree/request-broker";
import { Breadcrumbs, BreadcrumbsItem, Card, Select, Label, ListBox } from "@heroui/react";
import { useState, useRef, useEffect, useMemo, useCallback, createElement } from "react";
import { EnvironmentConfigurations, alerts } from "@livefree/runtime";
import { RestClient } from "@livefree/js-sdk";
import { Forbidden, SearchComboBox } from "@livefree/react-ui";
import { formatCurrencyUSD } from "@livefree/common";
import { FormField } from "@livefree/uniformly";
const reactive = new ReactiveCore("products");
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const db = new DexieDatabaseProvider([dexieRequestBrokerAddon]);
const ReactiveConfig = {
  db: {
    name: "livefree.products.db",
    provider: db,
    init: (db2) => {
      db2.version(1).stores(storeFormat(_store));
    }
  }
};
const _store = {};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const CatalogView = () => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
  /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Products" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Catalog" })
  ] }) }),
  /* @__PURE__ */ jsx(Card, { className: "flex-1", children: /* @__PURE__ */ jsx(Card.Content, { className: "p-3", children: /* @__PURE__ */ jsx("p", { className: "text-muted", children: "Products app scaffold." }) }) })
] });
const CatalogRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(CatalogView, {}) }) });
class ProductsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "products");
  }
  /** `GET /products/{id}` — rejects as not-found on a missing id (never 200-with-null). */
  async getById(id) {
    return await this.fetch(id);
  }
  /** `GET /products?category=` — every product in the category; unknown names answer empty. */
  async getByCategory(category) {
    return await this.get({ category });
  }
  /** `GET /products?sku=` — the base products carrying that SKU (variants excluded; SKU is not unique). */
  async getBySku(sku) {
    return await this.get({ sku });
  }
  /** `GET /products?search=&includeCategories=&excludeCategories=` — see {@link ProductSearchQuery}. */
  async search(query) {
    const params = {};
    const term = query.search?.trim();
    if (term) params.search = term;
    if (query.includeCategories?.length)
      params.includeCategories = query.includeCategories.join(",");
    if (query.excludeCategories?.length)
      params.excludeCategories = query.excludeCategories.join(",");
    return await this.get(params);
  }
  /** `GET /products/{id}/options` — the choices a configurable product offers; empty when it has none. */
  async getOptions(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/options`) });
  }
  /** `POST /products/{id}/variants` — the variant matching the options; rejects as not-found when none does. */
  async findVariant(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/variants`),
      body: request
    });
  }
  /** `GET /products/{id}/actions?eventType=` — the product's actions, all of them when no event type is given. */
  async getActions(id, eventType) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/actions`),
      query: eventType === void 0 ? void 0 : { eventType: String(eventType) }
    });
  }
}
const ProductsConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "ProductsAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "ProductsAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "ProductsAPI"
    }
  }
});
const apiHost = () => {
  const host = ProductsConfig.current?.api?.host;
  if (!host) throw new Error("No products API host is configured for this environment.");
  return host;
};
let products = null;
const getProductsClient = () => products ??= new ProductsClient(apiHost());
const EMPTY = {};
const NO_OPTIONS = {};
const ProductCatalog$1 = ({ categories, optionsFor = [], children }) => {
  const [byCategory, setByCategory] = useState(EMPTY);
  const [optionsByProduct, setOptionsByProduct] = useState(NO_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const key = categories.join("|");
  const optionsKey = optionsFor.join("|");
  const latestRequest = useRef(0);
  useEffect(() => {
    const names = key ? key.split("|") : [];
    const withOptions = new Set(optionsKey ? optionsKey.split("|") : []);
    const request = ++latestRequest.current;
    if (names.length === 0) {
      setByCategory(EMPTY);
      setOptionsByProduct(NO_OPTIONS);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const client = getProductsClient();
    Promise.all(names.map(async (name) => [name, await client.getByCategory(name)])).then(async (entries) => {
      const configurable = entries.filter(([name]) => withOptions.has(name)).flatMap(([, products2]) => products2);
      const options = await Promise.all(
        configurable.map(
          async (product) => [product.id, await client.getOptions(product.id)]
        )
      );
      if (request !== latestRequest.current) return;
      setByCategory(Object.fromEntries(entries));
      setOptionsByProduct(Object.fromEntries(options.map(([id, found]) => [id, found ?? []])));
    }).catch((error) => {
      if (request !== latestRequest.current) return;
      setByCategory(EMPTY);
      setOptionsByProduct(NO_OPTIONS);
      alerts.error(error instanceof Error ? error.message : "Could not load the product catalog");
    }).finally(() => {
      if (request === latestRequest.current) setIsLoading(false);
    });
  }, [key, optionsKey]);
  return /* @__PURE__ */ jsx(Fragment, { children: children({ byCategory, optionsByProduct, isLoading }) });
};
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "catalog/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "products", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CatalogRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "products", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CatalogRoutes, {}) })
    }
  )
] }) });
function getProductsNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: false,
    //user.hasApiAccess(apiKey),
    items: [
      {
        title: "Catalog",
        route: "/catalog",
        icon: "ri-price-tag-3-line",
        isVisible: user.hasPolicy(apiKey, "products", "view")
      }
    ]
  };
}
function getProductsResources() {
  return {
    items: [],
    handlers: ((_navigate) => ({}))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const productText = (product) => product.displayName || product.name || product.sku || `Product ${product.id}`;
const ProductResultRow = ({ product }) => /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center gap-3", children: [
  /* @__PURE__ */ jsx("span", { className: "w-16 flex-none font-mono text-[11px] text-[var(--muted)]", children: product.sku }),
  /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate", children: productText(product) }),
  /* @__PURE__ */ jsx("span", { className: "flex-none tabular-nums text-[var(--muted)]", children: formatCurrencyUSD(product.suggestedPrice ?? 0) })
] });
const toProductId = (value) => {
  if (value === null || value === void 0 || value === "") return null;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};
const useProductLookup = (productId, {
  isEnabled = true,
  errorMessage = "Could not load the selected product."
} = {}) => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const latestRequest = useRef(0);
  const loadedId = useRef(null);
  useEffect(() => {
    const id = toProductId(productId);
    if (id === null) {
      latestRequest.current += 1;
      loadedId.current = null;
      setProduct(null);
      setIsLoading(false);
      return;
    }
    if (!isEnabled) return;
    if (loadedId.current === id) return;
    const request = ++latestRequest.current;
    loadedId.current = id;
    setIsLoading(true);
    getProductsClient().getById(id).then((result) => {
      if (request === latestRequest.current) setProduct(result);
    }).catch((error) => {
      if (request !== latestRequest.current) return;
      loadedId.current = null;
      alerts.error(error instanceof Error ? error.message : errorMessage);
    }).finally(() => {
      if (request === latestRequest.current) setIsLoading(false);
    });
  }, [productId, isEnabled, errorMessage]);
  return { product, isLoading };
};
const defaultRenderItem = (product) => /* @__PURE__ */ jsx(ProductResultRow, { product });
const NONE$1 = [];
const ProductSearchComboBox$1 = ({
  includeCategories,
  excludeCategories,
  excludedProductIds = NONE$1,
  defaultValue = null,
  isLoading = false,
  onSelect,
  children,
  ...rest
}) => {
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const { product: initial, isLoading: isReading } = useProductLookup(defaultValue, {
    isEnabled: !hasSelected
  });
  const items = useMemo(() => {
    const list = results ?? (initial ? [initial] : null);
    if (!list || excludedProductIds.length === 0) return list;
    const excluded = new Set(excludedProductIds.map(Number));
    return list.filter((p) => !excluded.has(p.id));
  }, [results, initial, excludedProductIds]);
  const latestRequest = useRef(0);
  const handleSearch = useCallback(
    async (term) => {
      const request = ++latestRequest.current;
      setIsSearching(true);
      try {
        const found = await getProductsClient().search({
          search: term,
          includeCategories,
          excludeCategories
        });
        if (request !== latestRequest.current) return;
        setResults(found);
      } catch (error) {
        if (request !== latestRequest.current) return;
        setResults([]);
        alerts.error(error instanceof Error ? error.message : "Product search failed");
      } finally {
        if (request === latestRequest.current) setIsSearching(false);
      }
    },
    [includeCategories, excludeCategories]
  );
  const handleSelect = useCallback(
    (value, product) => {
      setHasSelected(true);
      onSelect?.(value === null || value === void 0 ? null : Number(value), product ?? null);
    },
    [onSelect]
  );
  return /* @__PURE__ */ jsx(
    SearchComboBox,
    {
      placeholder: "Search products...",
      emptyMessage: "No products matched that search.",
      minLength: 2,
      delay: 400,
      ...rest,
      defaultValue: defaultValue ?? void 0,
      items,
      isSearching: isSearching || isReading || isLoading,
      onSearch: handleSearch,
      onSelect: handleSelect,
      getItemValue: (product) => product.id,
      getItemText: (product) => productText(product),
      children: children ?? defaultRenderItem
    }
  );
};
const PRODUCT_SEARCH_COMBO_BOX = "products.product-search-combo-box";
const ProductSearchComboBox = (props) => createElement(
  Embed,
  null,
  createElement(ProductSearchComboBox$1, props)
);
const ProductSearchField$1 = FormField(
  ({
    name,
    value,
    onChange,
    showError,
    errors,
    // Uniformly's other injected props are pulled out so they are not forwarded to the combo box.
    isDirty,
    isValid,
    showValid,
    onlyDirty,
    ...rest
  }) => /* @__PURE__ */ jsx(
    ProductSearchComboBox$1,
    {
      name,
      defaultValue: typeof value === "string" || typeof value === "number" ? value : void 0,
      isInvalid: showError,
      errorMessage: errors?.[0],
      onSelect: (productId) => onChange(productId),
      ...rest
    }
  )
);
const PRODUCT_SEARCH_FIELD = "products.product-search-field";
const ProductSearchField = (props) => createElement(
  Embed,
  null,
  createElement(ProductSearchField$1, props)
);
const PRODUCT_CATALOG = "products.catalog";
const ProductCatalog = (props) => createElement(
  Embed,
  null,
  createElement(ProductCatalog$1, props)
);
const NONE = [];
const ProductOptionsPicker$1 = ({
  productId,
  onChange,
  className
}) => {
  const [loaded, setLoaded] = useState(null);
  const [chosen, setChosen] = useState({});
  const id = productId ? Number(productId) : null;
  const isLoading = id !== null && loaded?.productId !== id;
  const options = id !== null && loaded?.productId === id ? loaded.options : NONE;
  const latestRequest = useRef(0);
  const latestOnChange = useRef(onChange);
  useEffect(() => {
    latestOnChange.current = onChange;
  });
  useEffect(() => {
    const request = ++latestRequest.current;
    setChosen({});
    if (id === null) return;
    getProductsClient().getOptions(id).then((found) => {
      if (request === latestRequest.current) setLoaded({ productId: id, options: found ?? NONE });
    }).catch((error) => {
      if (request !== latestRequest.current) return;
      setLoaded({ productId: id, options: NONE });
      alerts.error(error instanceof Error ? error.message : "Could not load the product options");
    });
  }, [id]);
  const state = useMemo(() => {
    const selections = options.filter((option) => chosen[option.name]).map((option) => ({ typeName: option.name, valueName: chosen[option.name] }));
    const isComplete = !isLoading && options.every((option) => !option.required || chosen[option.name]);
    return { options, selections, isComplete, isLoading };
  }, [options, chosen, isLoading]);
  useEffect(() => {
    latestOnChange.current?.(state);
  }, [state]);
  if (isLoading || options.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: `grid grid-cols-1 gap-3 md:grid-cols-3 ${className ?? ""}`, children: options.map((option) => /* @__PURE__ */ jsxs(
    Select,
    {
      name: option.name,
      variant: "secondary",
      "aria-label": option.name,
      isRequired: option.required,
      value: chosen[option.name] ?? null,
      onChange: (value) => setChosen((current) => ({
        ...current,
        [option.name]: value == null ? "" : String(value)
      })),
      children: [
        /* @__PURE__ */ jsx(Label, { children: option.name }),
        /* @__PURE__ */ jsxs(Select.Trigger, { children: [
          /* @__PURE__ */ jsx(Select.Value, {}),
          /* @__PURE__ */ jsx(Select.Indicator, {})
        ] }),
        /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items: option.values, children: (value) => /* @__PURE__ */ jsxs(ListBox.Item, { id: value.name, textValue: value.name, children: [
          value.name,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, value.id) }) })
      ]
    },
    option.id
  )) });
};
const PRODUCT_OPTIONS_PICKER = "products.product-options-picker";
const ProductOptionsPicker = (props) => createElement(
  Embed,
  null,
  createElement(ProductOptionsPicker$1, props)
);
let appRuntime = null;
const initProductsRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("products", {
    description: {
      title: "Products",
      iconClass: "ri-price-tag-3-fill",
      defaultRoute: "/catalog",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getProductsNavigation,
    resources: getProductsResources,
    config: () => ProductsConfig.current
  });
  hostRuntime.components.register(PRODUCT_SEARCH_COMBO_BOX, ProductSearchComboBox);
  hostRuntime.components.register(PRODUCT_SEARCH_FIELD, ProductSearchField);
  hostRuntime.components.register(PRODUCT_CATALOG, ProductCatalog);
  hostRuntime.components.register(PRODUCT_OPTIONS_PICKER, ProductOptionsPicker);
};
export {
  initProductsRuntime
};
//# sourceMappingURL=index.js.map
